/* eslint no-console: 0 */
import { OutlinedInput } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/styles';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useSetModalTitle } from '../../../hooks/modal/useSetModalTitle.ts';
import { useGetAvailableServicePlans } from '../../../hooks/useGetAvailableServicePlans.ts';
import { useGetDevice } from '../../../hooks/useGetDevice.ts';
import { useReactivateDeviceMutation } from '../../../hooks/useReactivateDeviceMutation.ts';
import { describePlan } from '../../../services/device-utils-v2.ts';
import Button from '../../bits/Button.jsx';
import Loading from '../Loading.jsx';

const useStyles = makeStyles({
  snackbar: {
    position: 'absolute',
    bottom: 'calc(var(--app-safe-area-inset-bottom) + 64px)',
  },
  selectRoot: {
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#4a8bcc', // Focused border color
    },
  },
});

interface AlertComponentProps extends AlertProps {}
const Alert: React.FC<AlertComponentProps> = (props) => {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
};

const FormParagraph = styled.div`
  margin-top: 30px;
`;

const InfoBox = styled.div`
  background-color: rgb(253, 237, 237);
  color: rgb(95, 33, 32);
  padding: 8px;
  border-radius: 4px;
  display: block;
  margin-top: 30px;
`;

interface DeviceServicePlanRouteProps { }

interface ToastMessage {
  message: string | React.ReactNode;
  severity: 'error' | 'success';
}

export const DeviceServicePlanRoute: React.FC<DeviceServicePlanRouteProps> = () => {
  const classes = useStyles();
  const { id: deviceId } = useParams();

  const { data: device, isLoading: isDeviceLoading } = useGetDevice({ deviceId });
  const { data: availablePlans, isLoading: isAvailablePlansLoading } = useGetAvailableServicePlans({ deviceId });
  const isLoading = isDeviceLoading || isAvailablePlansLoading;
  const history = useHistory();
  const queryClient = useQueryClient();
  const mutation = useReactivateDeviceMutation();
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [toastMessage, setToastMessage] = useState<ToastMessage | undefined>(undefined);

  const cancelAt = device?.currentSubscription?.cancelAt;

  useSetModalTitle('Service Plan');

  useEffect(() => {
    const planId = device?.currentSubscription?.plan.id;
    if (planId) {
      setSelectedPlanId(planId);
    }
  }, [device, availablePlans]);

  const onServicePlanChange = useCallback(
    (e: React.ChangeEvent<{ name?: string; value: any }>) => {
      setSelectedPlanId(e.target.value)
    }, [setSelectedPlanId],
  );

  const showCancelButton = (device?.model === 'G1C' || device?.model === 'GTU30');

  /**
   * Handle form submission.
   */
  const onSubmit = useCallback(() => {
    
    mutation.mutate({ deviceId, planId: selectedPlanId }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['useGetDevice', deviceId] });
        queryClient.setQueryData(['useGetDevice', deviceId], null);
        setToastMessage({ 
          severity: 'success', 
          message: 'Device reactivated successfully.',
        });
      },
      onError: () => {
        // For now lets alert of a generic error and instruct user that the issue may be there
        // billing info.
        setToastMessage({ 
          severity: 'error',
          message: (
            <>
              Unable to reactivate device. Please{' '}
              <Link key="billing-link" style={{ color: 'white', textDecoration: 'underline' }} to={{
                pathname: '/m/billing',
                state: { origin: 'service_plan' },
              }}>update your billing details</Link>{' '}
              and try again.
            </>
          ),
        });
      },
    });
  }, [mutation, selectedPlanId, deviceId, queryClient, history]);

  const onHideToast = useCallback(() => {
    setToastMessage(undefined);
  }, []);

  const onCancelPlanClick = useCallback(() => {
    history.push(
      `/m/devices/${deviceId}/plan/cancel`,
    { origin: 'service_plan' });
  }, [history]);

  // Technically upon activation a device subscription is set synchronously, but the device state
  // is set asynchronously (on response from Stripe). So we need to check both.
  const isDeviceActive = device?.state === 'active' || !!device?.currentSubscription
  const buttonTitle = isDeviceActive ? 'Change Plan' : 'Reactivate Device';

  return (
    <>
      {isLoading && <Loading />}

      {!isLoading && device && <>

        <FormControl variant="outlined" fullWidth>
          <InputLabel htmlFor="outlined-serviceplan-native-simple">Plan</InputLabel>
          <Select
            fullWidth={true}
            disabled={isDeviceActive}
            native
            value={selectedPlanId}
            onChange={onServicePlanChange}
            label="Plan"
            input={
              <OutlinedInput
                label="Plan"
                id="outlined-serviceplan-native-simple"
                classes={{ root: classes.selectRoot }}
              />
            }
          >
            <option aria-label="None" value=""></option>
            {availablePlans && availablePlans.map(plan => <option key={plan.id} value={plan.id}>{describePlan(plan)}</option>)}
          </Select>
        </FormControl>

        {!!cancelAt && <InfoBox>
          Your plan is scheduled to cancel on <strong>{new Date(cancelAt).toLocaleDateString()}</strong>.
        </InfoBox>}

        {isDeviceActive &&
          <FormParagraph>To change your current service plan, please contact our support team using the
            <Link
              to={{
                pathname: `/m/help/contact-us`,
                state: { origin: 'service_plan' },
              }}> Help Form.</Link>
          </FormParagraph>}

        <div style={{ marginTop: '30px' }}>
          <Button
            onClick={onSubmit}
            color="primary"
            disabled={isDeviceActive || selectedPlanId === '' || mutation.isLoading}
            loading={mutation.isLoading}>{buttonTitle}</Button>
        </div>

        {showCancelButton &&
          <div style={{ marginTop: '20px' }}>
            <Button
              onClick={onCancelPlanClick}
              color="danger"
              disabled={!isDeviceActive || mutation.isLoading || !!cancelAt}
              loading={mutation.isLoading}>Cancel Plan</Button>
          </div>
    }
      </>}

      <Snackbar
        open={!!toastMessage}
        autoHideDuration={6000}
        onClose={onHideToast}
        className={classes.snackbar}
      >
        { !!toastMessage ? (
          <Alert onClose={onHideToast} severity={toastMessage?.severity}>
            {toastMessage?.message}
          </Alert> 
        ) : undefined}
      </Snackbar>
    </>
  );
};
